import Downshift from 'downshift';
import { Cancel, ArrowDown, ArrowUp } from '@jsluna/icons';
import { Button } from '@jsluna/button';
import { Input, InputGroup, InputControl, InputAction, InputIcon, FormGroup } from '@jsluna/form';
import classnames from 'classnames';
import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { onKeyPress, filterProps, debounce } from '@jsluna/utils';
import { ProgressIndicator, ProgressSpinner, ProgressMessage } from '@jsluna/progress';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

var AutocompleteInputGroup = function AutocompleteInputGroup(_ref) {
  var getInputProps = _ref.getInputProps,
      getToggleButtonProps = _ref.getToggleButtonProps,
      getClearButtonProps = _ref.getClearButtonProps,
      selectedOption = _ref.selectedOption,
      selectedOptions = _ref.selectedOptions,
      isOpen = _ref.isOpen,
      placeholder = _ref.placeholder,
      describedBy = _ref.describedBy,
      onChange = _ref.onChange,
      getOptionLabel = _ref.getOptionLabel,
      getOptionValue = _ref.getOptionValue,
      icon = _ref.icon,
      action = _ref.action,
      hideInput = _ref.hideInput,
      autoComplete = _ref.autoComplete,
      disabled = _ref.disabled,
      spellcheck = _ref.spellcheck,
      name = _ref.name,
      rest = _objectWithoutPropertiesLoose(_ref, ["getInputProps", "getToggleButtonProps", "getClearButtonProps", "selectedOption", "selectedOptions", "isOpen", "placeholder", "describedBy", "onChange", "getOptionLabel", "getOptionValue", "icon", "action", "hideInput", "autoComplete", "disabled", "spellcheck", "name"]);

  var selectedOptionsLength = selectedOptions.length;
  var isMultiple = selectedOptionsLength > 0;
  var clearButtonProps = getClearButtonProps();

  var handleBackspace = function handleBackspace(e) {
    if (e.target.value.length > 0 && !selectedOption) {
      return true;
    }

    if (isMultiple) {
      return clearButtonProps.onClick(selectedOptions[selectedOptionsLength - 1]);
    }

    return clearButtonProps.onClick();
  };

  var inputProps = getInputProps({
    onChange: onChange,
    onKeyDown: onKeyPress([{
      name: 'Backspace',
      code: 8
    }], handleBackspace),
    role: 'textbox',
    disabled: disabled
  });
  return /*#__PURE__*/React.createElement(InputGroup, {
    disabled: disabled
  }, icon && /*#__PURE__*/React.createElement(InputIcon, null, icon), /*#__PURE__*/React.createElement(InputControl, null, /*#__PURE__*/React.createElement("div", {
    className: classnames('ln-c-autocomplete', isOpen && 'is-open')
  }, isMultiple && selectedOptions.map(function (item) {
    return /*#__PURE__*/React.createElement("span", {
      key: getOptionValue(item),
      className: "ln-c-autocomplete__selected-item"
    }, getOptionLabel(item), /*#__PURE__*/React.createElement("button", _extends({
      type: "button",
      className: "ln-c-autocomplete__clear-selected-item"
    }, clearButtonProps, {
      onClick: function onClick() {
        return clearButtonProps.onClick(item);
      }
    }), /*#__PURE__*/React.createElement(Cancel, {
      fixed: true
    })));
  }), !hideInput && /*#__PURE__*/React.createElement(Input, _extends({}, rest, {
    className: "ln-c-autocomplete__input"
  }, inputProps, {
    value: !isMultiple && selectedOption ? getOptionLabel(selectedOption) : inputProps.value,
    placeholder: placeholder,
    "aria-describedby": describedBy,
    autoComplete: autoComplete,
    spellCheck: spellcheck,
    id: name,
    name: name
  })))), (!hideInput && action || selectedOption) && /*#__PURE__*/React.createElement(InputAction, null, selectedOption ? /*#__PURE__*/React.createElement(Button, clearButtonProps, /*#__PURE__*/React.createElement(Cancel, null)) : action && /*#__PURE__*/React.createElement(Button, getToggleButtonProps({
    disabled: disabled
  }), isOpen ? /*#__PURE__*/React.createElement(ArrowUp, null) : /*#__PURE__*/React.createElement(ArrowDown, null))));
};

AutocompleteInputGroup.propTypes = {
  getInputProps: PropTypes.func.isRequired,
  getToggleButtonProps: PropTypes.func.isRequired,
  getClearButtonProps: PropTypes.func.isRequired,
  selectedOption: PropTypes.shape({}),
  selectedOptions: PropTypes.arrayOf(PropTypes.object),
  isOpen: PropTypes.bool,
  placeholder: PropTypes.string,
  describedBy: PropTypes.string,
  onChange: PropTypes.func,
  getOptionLabel: PropTypes.func.isRequired,
  getOptionValue: PropTypes.func.isRequired,
  icon: PropTypes.node,
  action: PropTypes.bool,
  hideInput: PropTypes.bool,
  autoComplete: PropTypes.string,
  disabled: PropTypes.bool,
  spellcheck: PropTypes.bool,
  name: PropTypes.string
};
AutocompleteInputGroup.defaultProps = {
  selectedOption: undefined,
  selectedOptions: [],
  isOpen: false,
  placeholder: undefined,
  describedBy: undefined,
  onChange: undefined,
  icon: undefined,
  action: true,
  hideInput: false,
  autoComplete: 'off',
  disabled: false,
  spellcheck: false,
  name: undefined
};
AutocompleteInputGroup.displayName = 'AutocompleteInputGroup';

var ResultList = function ResultList(_ref) {
  var getOptionLabel = _ref.getOptionLabel,
      getOptionValue = _ref.getOptionValue,
      getMenuProps = _ref.getMenuProps,
      getItemProps = _ref.getItemProps,
      options = _ref.options,
      isOpen = _ref.isOpen,
      selectedItem = _ref.selectedItem,
      highlightedIndex = _ref.highlightedIndex,
      optionRenderer = _ref.optionRenderer,
      verticalAlign = _ref.verticalAlign,
      className = _ref.className,
      noResultsMessage = _ref.noResultsMessage,
      rest = _objectWithoutPropertiesLoose(_ref, ["getOptionLabel", "getOptionValue", "getMenuProps", "getItemProps", "options", "isOpen", "selectedItem", "highlightedIndex", "optionRenderer", "verticalAlign", "className", "noResultsMessage"]);

  return /*#__PURE__*/React.createElement("ul", _extends({}, rest, getMenuProps(), {
    className: classnames('ln-c-dropdown-list', 'ln-c-dropdown-list--overlay', className)
  }), isOpen && (options.length > 0 ?
  /* eslint-disable jsx-a11y/role-supports-aria-props */
  options.map(function (option, index) {
    return /*#__PURE__*/React.createElement("li", _extends({
      className: classnames('ln-c-dropdown-list__item', verticalAlign && "ln-c-dropdown-list__item--align-" + verticalAlign, selectedItem === option && 'is-selected', highlightedIndex === index && 'is-hover')
    }, getItemProps({
      key: getOptionValue(option),
      item: option,
      index: index
    }), {
      "aria-selected": selectedItem === option || undefined
    }), optionRenderer ? optionRenderer(option) : getOptionLabel(option));
  }) : /*#__PURE__*/React.createElement("li", {
    className: classnames('ln-c-dropdown-list__item', 'ln-c-dropdown-list__item--message', verticalAlign && "ln-c-dropdown-list__item--align-" + verticalAlign)
  }, noResultsMessage)));
};

ResultList.propTypes = {
  getOptionLabel: PropTypes.func.isRequired,
  getOptionValue: PropTypes.func.isRequired,
  getMenuProps: PropTypes.func.isRequired,
  getItemProps: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.object),
  isOpen: PropTypes.bool,
  optionRenderer: PropTypes.func,
  selectedItem: PropTypes.shape({}),
  highlightedIndex: PropTypes.number,
  verticalAlign: PropTypes.string,
  className: PropTypes.string,
  noResultsMessage: PropTypes.node
};
ResultList.defaultProps = {
  options: [],
  isOpen: false,
  optionRenderer: undefined,
  selectedItem: undefined,
  highlightedIndex: undefined,
  verticalAlign: 'center',
  className: undefined,
  noResultsMessage: 'No results found'
};
ResultList.displayName = 'ResultList';

var HiddenInput = function HiddenInput(_ref) {
  var multiSelect = _ref.multiSelect,
      name = _ref.name,
      value = _ref.value;

  if (multiSelect) {
    return /*#__PURE__*/React.createElement(Fragment, null, value.length > 0 ? value.map(function (optionValue) {
      return /*#__PURE__*/React.createElement("input", {
        key: optionValue,
        name: name + "[]",
        type: "hidden",
        value: optionValue
      });
    }) : /*#__PURE__*/React.createElement("input", {
      name: name + "[]",
      type: "hidden",
      value: ""
    }));
  }

  return /*#__PURE__*/React.createElement("input", {
    name: name,
    type: "hidden",
    value: value
  });
};

HiddenInput.propTypes = {
  multiSelect: PropTypes.bool,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array])
};
HiddenInput.defaultProps = {
  multiSelect: false,
  value: ''
};
HiddenInput.displayName = 'HiddenInput';

var getOptionLabel = function getOptionLabel(option) {
  return option ? option.label : '';
};

var getOptionValue = function getOptionValue(option) {
  return option ? option.value : '';
};

var matchOperators = /[|\\{}()[\]^$+*?.]/g;

var escapeStringRegExp = function escapeStringRegExp(str) {
  if (typeof str !== 'string') {
    throw new TypeError('Expected a string');
  }

  return str.replace(matchOperators, '\\$&');
};

var createFilter = function createFilter(inputValue, getOptionLabel) {
  return function (option) {
    return new RegExp(escapeStringRegExp(inputValue), 'gi').test(getOptionLabel(option));
  };
};

var AutocompleteFormGroup = function AutocompleteFormGroup(_ref) {
  var name = _ref.name,
      required = _ref.required,
      optional = _ref.optional,
      multiSelect = _ref.multiSelect,
      async = _ref.async,
      selectedOptions = _ref.selectedOptions,
      onClear = _ref.onClear,
      placeholder = _ref.placeholder,
      getOptionLabel$$1 = _ref.getOptionLabel,
      getOptionValue$$1 = _ref.getOptionValue,
      icon = _ref.icon,
      action = _ref.action,
      onChange = _ref.onChange,
      options = _ref.options,
      filter = _ref.filter,
      value = _ref.value,
      optionRenderer = _ref.optionRenderer,
      noResultsMessage = _ref.noResultsMessage,
      label = _ref.label,
      labelElement = _ref.labelElement,
      hideLabel = _ref.hideLabel,
      error = _ref.error,
      warning = _ref.warning,
      info = _ref.info,
      disabled = _ref.disabled,
      className = _ref.className,
      validationFirst = _ref.validationFirst,
      element = _ref.element,
      spellcheck = _ref.spellcheck,
      getInputProps = _ref.getInputProps,
      getItemProps = _ref.getItemProps,
      getLabelProps = _ref.getLabelProps,
      getMenuProps = _ref.getMenuProps,
      isOpen = _ref.isOpen,
      inputValue = _ref.inputValue,
      highlightedIndex = _ref.highlightedIndex,
      selectedItem = _ref.selectedItem,
      getToggleButtonProps = _ref.getToggleButtonProps,
      clearSelection = _ref.clearSelection,
      rest = _objectWithoutPropertiesLoose(_ref, ["name", "required", "optional", "multiSelect", "async", "selectedOptions", "onClear", "placeholder", "getOptionLabel", "getOptionValue", "icon", "action", "onChange", "options", "filter", "value", "optionRenderer", "noResultsMessage", "label", "labelElement", "hideLabel", "error", "warning", "info", "disabled", "className", "validationFirst", "element", "spellcheck", "getInputProps", "getItemProps", "getLabelProps", "getMenuProps", "isOpen", "inputValue", "highlightedIndex", "selectedItem", "getToggleButtonProps", "clearSelection"]);

  return /*#__PURE__*/React.createElement(FormGroup, {
    labelElement: labelElement,
    name: name,
    required: required,
    optional: optional,
    label: label,
    hideLabel: hideLabel,
    error: error,
    warning: warning,
    info: info,
    className: className,
    validationFirst: validationFirst,
    element: element
  }, function (inputProps) {
    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(HiddenInput, {
      name: name,
      multiSelect: multiSelect,
      value: multiSelect ? selectedOptions.map(function (option) {
        return getOptionValue$$1(option);
      }) : getOptionValue$$1(selectedItem)
    }), /*#__PURE__*/React.createElement(AutocompleteInputGroup, _extends({}, inputProps, {
      getInputProps: getInputProps,
      getToggleButtonProps: getToggleButtonProps,
      getClearButtonProps: function getClearButtonProps(props) {
        return Object.assign({
          onClick: onClear || clearSelection,
          'aria-label': 'Clear the field'
        }, props);
      },
      selectedOption: !multiSelect ? selectedItem : undefined,
      selectedOptions: selectedOptions,
      isOpen: isOpen,
      optional: optional,
      placeholder: placeholder,
      getOptionLabel: getOptionLabel$$1,
      getOptionValue: getOptionValue$$1,
      icon: icon,
      action: action,
      onChange: onChange,
      hideInput: !async && options.length === 0,
      disabled: disabled,
      spellCheck: spellcheck,
      name: name
    }, rest)), /*#__PURE__*/React.createElement(ResultList, {
      getMenuProps: getMenuProps,
      getItemProps: getItemProps,
      selectedItem: selectedItem,
      isOpen: isOpen,
      highlightedIndex: highlightedIndex,
      options: options.filter(filter(value || inputValue, getOptionLabel$$1)),
      optionRenderer: optionRenderer,
      getOptionLabel: getOptionLabel$$1,
      getOptionValue: getOptionValue$$1,
      noResultsMessage: noResultsMessage
    }));
  });
};

AutocompleteFormGroup.propTypes = Object.assign({}, FormGroup.propTypes, {
  options: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  optionRenderer: PropTypes.func,
  hideLabel: PropTypes.bool,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  info: PropTypes.node,
  disabled: PropTypes.bool,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  filter: PropTypes.func,
  placeholder: PropTypes.string,
  icon: PropTypes.node,
  action: PropTypes.bool,
  multiSelect: PropTypes.bool,
  async: PropTypes.bool,
  value: PropTypes.string,
  selectedOptions: PropTypes.arrayOf(PropTypes.object),
  onClear: PropTypes.func,
  onSelect: PropTypes.func,
  spellcheck: PropTypes.bool,
  getInputProps: PropTypes.func.isRequired,
  getItemProps: PropTypes.func.isRequired,
  getLabelProps: PropTypes.func.isRequired,
  getMenuProps: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  inputValue: PropTypes.string,
  highlightedIndex: PropTypes.number,
  selectedItem: PropTypes.shape({}),
  getToggleButtonProps: PropTypes.func.isRequired,
  clearSelection: PropTypes.func.isRequired
});
AutocompleteFormGroup.defaultProps = {
  options: [],
  onChange: undefined,
  optionRenderer: undefined,
  hideLabel: false,
  required: undefined,
  optional: undefined,
  info: undefined,
  disabled: false,
  getOptionLabel: getOptionLabel,
  getOptionValue: getOptionValue,
  filter: createFilter,
  placeholder: undefined,
  icon: undefined,
  action: undefined,
  multiSelect: false,
  async: false,
  value: undefined,
  selectedOptions: [],
  onClear: undefined,
  onSelect: undefined,
  isOpen: false,
  inputValue: undefined,
  highlightedIndex: undefined,
  selectedItem: undefined,
  spellcheck: false
};
AutocompleteFormGroup.displayName = 'AutocompleteFormGroup';

var AutocompleteField = function AutocompleteField(_ref) {
  var Element = _ref.element,
      onSelect = _ref.onSelect,
      defaultSelectedOption = _ref.defaultSelectedOption,
      getOptionLabel$$1 = _ref.getOptionLabel,
      value = _ref.value,
      spellcheck = _ref.spellcheck,
      disabled = _ref.disabled,
      filter = _ref.filter,
      props = _objectWithoutPropertiesLoose(_ref, ["element", "onSelect", "defaultSelectedOption", "getOptionLabel", "value", "spellcheck", "disabled", "filter"]);

  return /*#__PURE__*/React.createElement(Downshift, {
    itemToString: getOptionLabel$$1,
    onChange: onSelect,
    inputValue: value,
    defaultSelectedItem: defaultSelectedOption
  }, function (_ref2) {
    var getInputProps = _ref2.getInputProps,
        getItemProps = _ref2.getItemProps,
        getLabelProps = _ref2.getLabelProps,
        getMenuProps = _ref2.getMenuProps,
        isOpen = _ref2.isOpen,
        inputValue = _ref2.inputValue,
        highlightedIndex = _ref2.highlightedIndex,
        selectedItem = _ref2.selectedItem,
        getToggleButtonProps = _ref2.getToggleButtonProps,
        clearSelection = _ref2.clearSelection;
    return /*#__PURE__*/React.createElement(Element, null, /*#__PURE__*/React.createElement(AutocompleteFormGroup, _extends({
      getInputProps: getInputProps,
      getItemProps: getItemProps,
      getLabelProps: getLabelProps,
      getMenuProps: getMenuProps,
      isOpen: isOpen,
      inputValue: inputValue,
      highlightedIndex: highlightedIndex,
      selectedItem: selectedItem,
      getToggleButtonProps: getToggleButtonProps,
      clearSelection: clearSelection,
      getOptionLabel: getOptionLabel$$1,
      value: value,
      disabled: disabled,
      filter: filter,
      spellcheck: spellcheck
    }, props)));
  });
};

AutocompleteField.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  defaultSelectedOption: PropTypes.shape({}),
  disabled: PropTypes.bool,
  filter: PropTypes.func,
  getOptionLabel: PropTypes.func,
  onSelect: PropTypes.func,
  spellcheck: PropTypes.bool,
  value: PropTypes.string
};
AutocompleteField.defaultProps = {
  element: 'div',
  defaultSelectedOption: undefined,
  disabled: false,
  filter: createFilter,
  getOptionLabel: getOptionLabel,
  onSelect: undefined,
  spellcheck: true,
  value: undefined
};
AutocompleteField.displayName = 'AutocompleteField';

var MultiAutocompleteField = /*#__PURE__*/function (_Component) {
  _inheritsLoose(MultiAutocompleteField, _Component);

  function MultiAutocompleteField(props) {
    var _this;

    var defaultSelectedOptions = props.defaultSelectedOptions;
    _this = _Component.call(this, props) || this;
    _this.state = {
      inputValue: '',
      selectedOptions: defaultSelectedOptions
    };
    _this.handleClear = _this.handleClear.bind(_assertThisInitialized(_this));
    _this.handleInputChange = _this.handleInputChange.bind(_assertThisInitialized(_this));
    _this.handleSelect = _this.handleSelect.bind(_assertThisInitialized(_this));
    _this.filterSelectedOptions = _this.filterSelectedOptions.bind(_assertThisInitialized(_this));
    return _this;
  }

  var _proto = MultiAutocompleteField.prototype;

  _proto.handleClear = function handleClear(selectedOption) {
    var selectedOptions = this.state.selectedOptions;
    var onSelect = this.props.onSelect;
    var newSelectedOptions = selectedOptions.filter(function (option) {
      return option !== selectedOption;
    });
    this.setState({
      inputValue: '',
      selectedOptions: newSelectedOptions
    });

    if (onSelect) {
      onSelect(newSelectedOptions);
    }
  };

  _proto.handleInputChange = function handleInputChange(e) {
    var onChange = this.props.onChange;
    this.setState({
      inputValue: e.target.value
    });

    if (onChange) {
      onChange(e);
    }
  };

  _proto.handleSelect = function handleSelect(selectedOption) {
    var selectedOptions = this.state.selectedOptions;
    var onSelect = this.props.onSelect;
    var newSelectedOptions = [].concat(selectedOptions, [selectedOption]);
    this.setState({
      inputValue: '',
      selectedOptions: newSelectedOptions
    });

    if (onSelect) {
      onSelect(newSelectedOptions);
    }
  };

  _proto.filterSelectedOptions = function filterSelectedOptions(option) {
    var selectedOptions = this.state.selectedOptions;
    var getOptionValue$$1 = this.props.getOptionValue;
    return !selectedOptions.find(function (selectedOption) {
      return getOptionValue$$1(selectedOption) === getOptionValue$$1(option);
    });
  };

  _proto.render = function render() {
    var _this$state = this.state,
        selectedOptions = _this$state.selectedOptions,
        inputValue = _this$state.inputValue;
    var options = this.props.options;
    return /*#__PURE__*/React.createElement(AutocompleteField, _extends({}, filterProps(this.props, ['defaultSelectedOptions']), {
      options: options.filter(this.filterSelectedOptions),
      multiSelect: true,
      value: inputValue,
      selectedOptions: selectedOptions,
      onClear: this.handleClear,
      onSelect: this.handleSelect,
      onChange: this.handleInputChange
    }));
  };

  return MultiAutocompleteField;
}(Component);

MultiAutocompleteField.propTypes = {
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.object),
  defaultSelectedOptions: PropTypes.arrayOf(PropTypes.object),
  getOptionValue: PropTypes.func
};
MultiAutocompleteField.defaultProps = {
  getOptionValue: getOptionValue,
  onChange: undefined,
  onSelect: undefined,
  options: [],
  defaultSelectedOptions: []
};
MultiAutocompleteField.displayName = 'MultiAutocompleteField';

var LoadingMessage = function LoadingMessage() {
  return /*#__PURE__*/React.createElement(ProgressIndicator, {
    loading: true,
    preventFocus: true
  }, /*#__PURE__*/React.createElement(ProgressSpinner, null), /*#__PURE__*/React.createElement(ProgressMessage, {
    className: "ln-u-margin-left"
  }, "Loading..."));
};

LoadingMessage.displayName = 'LoadingMessage';

var AsyncAutocompleteField = /*#__PURE__*/function (_Component) {
  _inheritsLoose(AsyncAutocompleteField, _Component);

  function AsyncAutocompleteField(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this.state = {
      options: [],
      loading: false,
      inputValue: ''
    };
    _this.handleInputChange = _this.handleInputChange.bind(_assertThisInitialized(_this));
    _this.getOptions = debounce(_this.getOptions.bind(_assertThisInitialized(_this)), props.debounceWait);
    _this.getNoResultsMessage = _this.getNoResultsMessage.bind(_assertThisInitialized(_this));
    _this.resetOptions = _this.resetOptions.bind(_assertThisInitialized(_this));
    _this.handleSelect = _this.handleSelect.bind(_assertThisInitialized(_this));
    return _this;
  }

  var _proto = AsyncAutocompleteField.prototype;

  _proto.componentDidUpdate = function componentDidUpdate(_, prevState) {
    var minChars = this.props.minChars;
    var inputValue = this.state.inputValue;
    var prevInputValue = prevState.inputValue;

    if (inputValue !== prevInputValue) {
      if (inputValue.length < minChars) {
        this.resetOptions();
        return;
      }

      this.getOptions(inputValue);
    }
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    if (this.getOptions) {
      this.getOptions.cancel();
    }
  };

  _proto.getOptions = function getOptions() {
    var _this2 = this;

    var loadOptions = this.props.loadOptions;
    var inputValue = this.state.inputValue;
    this.setState({
      loading: true
    });
    loadOptions(inputValue).then(function (options) {
      _this2.setState({
        options: options,
        loading: false
      });
    });
  };

  _proto.getNoResultsMessage = function getNoResultsMessage() {
    var _this$state = this.state,
        inputValue = _this$state.inputValue,
        loading = _this$state.loading;
    var _this$props = this.props,
        minChars = _this$props.minChars,
        placeholderMessage = _this$props.placeholderMessage,
        loadingMessage = _this$props.loadingMessage,
        noResultsMessage = _this$props.noResultsMessage;

    if (inputValue.length < minChars) {
      return placeholderMessage;
    }

    if (loading) {
      return loadingMessage;
    }

    return noResultsMessage;
  };

  _proto.handleInputChange = function handleInputChange(e) {
    var onChange = this.props.onChange;
    var value = e.target.value;
    this.setState({
      inputValue: value
    });

    if (onChange) {
      onChange(e);
    }
  };

  _proto.handleSelect = function handleSelect(e) {
    var onSelect = this.props.onSelect;

    if (onSelect) {
      onSelect(e);
    }
  };

  _proto.resetOptions = function resetOptions() {
    this.setState({
      options: []
    });
  };

  _proto.render = function render() {
    var _this$state2 = this.state,
        inputValue = _this$state2.inputValue,
        options = _this$state2.options;
    var children = this.props.children;
    var props = Object.assign({}, filterProps(this.props, ['loadOptions', 'debounceWait', 'children', 'minChars', 'placeholderMessage', 'loadingMessage']), {
      inputValue: inputValue,
      async: true,
      options: options,
      onChange: this.handleInputChange,
      noResultsMessage: this.getNoResultsMessage(),
      onSelect: this.handleSelect
    });

    if (children) {
      return React.cloneElement(React.Children.only(children), props);
    }

    return /*#__PURE__*/React.createElement(AutocompleteField, props);
  };

  return AsyncAutocompleteField;
}(Component);

AsyncAutocompleteField.propTypes = {
  loadOptions: PropTypes.func.isRequired,
  minChars: PropTypes.number,
  debounceWait: PropTypes.number,
  children: PropTypes.node,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  placeholderMessage: PropTypes.node,
  loadingMessage: PropTypes.node,
  noResultsMessage: PropTypes.node,
  filter: PropTypes.func
};
AsyncAutocompleteField.defaultProps = {
  minChars: 2,
  debounceWait: 200,
  children: undefined,
  onChange: undefined,
  onSelect: undefined,
  placeholderMessage: 'Start typing to search',
  loadingMessage: /*#__PURE__*/React.createElement(LoadingMessage, null),
  noResultsMessage: undefined,
  filter: createFilter
};
AsyncAutocompleteField.displayName = 'AsyncAutocompleteField';
export { AutocompleteField, MultiAutocompleteField, AsyncAutocompleteField };